import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Select,
  MenuItem,
  FormControl,
  ListItemButton,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { MvpCity, MVP_CITIES } from '../types';

const ProfileScreen: React.FC = () => {
  const { selectedCity, setSelectedCity } = useApp();
  const { user, logout } = useAuth();
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [locationEnabled, setLocationEnabled] = useState(true);
  const [darkModeEnabled, setDarkModeEnabled] = useState(false);

  const handleSignOut = () => {
    logout();
  };

  // Get initials for avatar
  const getInitials = () => {
    if (user?.fullName) {
      const names = user.fullName.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Get display name
  const getDisplayName = () => {
    return user?.fullName || 'User';
  };

  // Get display email
  const getDisplayEmail = () => {
    return user?.email || '';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <Header title="Profile" showBackButton={false} />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Profile Header */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            py: 4,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              bgcolor: 'primary.main',
              fontSize: 32,
              mb: 2,
            }}
          >
            {getInitials()}
          </Avatar>
          <Typography variant="h6" fontWeight={600}>
            {getDisplayName()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getDisplayEmail()}
          </Typography>
        </Box>

        {/* City Selector */}
        <Box sx={{ p: 2, bgcolor: 'background.paper', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Your City
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value as MvpCity)}
              sx={{ borderRadius: 2 }}
            >
              {MVP_CITIES.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Settings */}
        <Box sx={{ bgcolor: 'background.paper', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, py: 1 }}>
            Settings
          </Typography>
          <List disablePadding>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <NotificationsIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary="Push Notifications" 
                secondary="Get notified about new sales"
              />
              <Switch 
                checked={notificationsEnabled}
                onChange={(e) => setNotificationsEnabled(e.target.checked)}
                color="primary"
              />
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LocationOnIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary="Location Services" 
                secondary="Enable for nearby sales"
              />
              <Switch 
                checked={locationEnabled}
                onChange={(e) => setLocationEnabled(e.target.checked)}
                color="primary"
              />
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DarkModeIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary="Dark Mode" 
                secondary="Coming soon"
              />
              <Switch 
                checked={darkModeEnabled}
                onChange={(e) => setDarkModeEnabled(e.target.checked)}
                color="primary"
                disabled
              />
            </ListItem>
          </List>
        </Box>

        {/* Support */}
        <Box sx={{ bgcolor: 'background.paper', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, py: 1 }}>
            Support
          </Typography>
          <List disablePadding>
            <ListItemButton>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <HelpOutlineIcon color="action" />
              </ListItemIcon>
              <ListItemText primary="Help Center" />
            </ListItemButton>
            <Divider variant="inset" component="li" />
            <ListItemButton>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <InfoOutlinedIcon color="action" />
              </ListItemIcon>
              <ListItemText primary="About" />
            </ListItemButton>
            <Divider variant="inset" component="li" />
            <ListItemButton onClick={handleSignOut}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon color="action" />
              </ListItemIcon>
              <ListItemText primary="Sign Out" />
            </ListItemButton>
          </List>
        </Box>

        {/* App Version */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            YardApp v1.0.0
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileScreen;